/// <reference types="vite/client" />
import client from "./client";
import type { ChatCsv, ChatImage, DatasourceOption, StreamDoneMeta, ThinkingStep } from "../types/chat";

function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
}

export const chatApi = {
  sendMessage: (message: string, conversationId?: string | null) =>
    client.post(
      "/chat/message",
      { message, conversation_id: conversationId },
      { headers: { "X-User-Timezone": getUserTimezone() } },
    ),

  listConversations: () => client.get("/chat/conversations"),

  getConversation: (id: string) => client.get(`/chat/conversations/${id}`),

  deleteConversation: (id: string) => client.delete(`/chat/conversations/${id}`),

  renameConversation: (id: string, title: string) => client.patch(`/chat/conversations/${id}`, { title }),

  getSuggestedQuestions: () => client.get("/chat/suggested-questions"),

  listDatasources: () => client.get<DatasourceOption[]>("/chat/datasources"),

  generateSql: (description: string, datasource_key: string | null = null) =>
    client.post("/chat/generate-sql", { description, datasource_key }),
};

export type StreamMessageOptions = {
  message: string;
  conversationId?: string | null;
  images?: ChatImage[];
  csvs?: ChatCsv[];
  datasourceKeys?: string[];
  teammateId?: string | null;
  onThinking?: (data: ThinkingStep) => void;
  onDelta?: (text: string) => void;
  onDone?: (meta: StreamDoneMeta) => void;
  onError?: (msg: string) => void;
};

/**
 * Opens an SSE stream to /chat/message/stream.
 * Returns a cleanup function that aborts the stream. Handles 401 by attempting
 * one token refresh via the axios client before retrying the stream request.
 */
export function streamMessage({
  message,
  conversationId,
  images = [],
  csvs = [],
  datasourceKeys,
  teammateId,
  onThinking,
  onDelta,
  onDone,
  onError,
}: StreamMessageOptions) {
  const baseURL = import.meta.env.VITE_API_URL || "/api";
  const ctrl = new AbortController();

  async function _openStream(retried = false): Promise<void> {
    const token = localStorage.getItem("access_token");
    const res = await fetch(`${baseURL}/chat/message/stream`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "X-User-Timezone": getUserTimezone(),
      },
      body: JSON.stringify({
        message,
        conversation_id: conversationId ?? null,
        images: images.map((img) => ({ media_type: img.mediaType, data: img.base64 })),
        csvs: csvs.map((csv) => ({ filename: csv.filename, content: csv.content })),
        datasource_keys: datasourceKeys?.length ? datasourceKeys : null,
        teammate_id: teammateId ?? null,
      }),
      signal: ctrl.signal,
    });

    if (res.status === 401 && !retried) {
      try {
        await client.get("/auth/me");
      } catch {
        return;
      }
      return _openStream(true);
    }

    if (!res.ok) {
      onError?.(`HTTP ${res.status}`);
      return;
    }
    if (!res.body) {
      onError?.("No response stream");
      return;
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });

      const parts = buffer.split("\n\n");
      buffer = parts.pop() ?? "";

      for (const part of parts) {
        const eventLine = part.split("\n").find((l) => l.startsWith("event:"));
        const dataLine = part.split("\n").find((l) => l.startsWith("data:"));
        if (!dataLine) continue;

        const event = eventLine ? eventLine.slice(6).trim() : "message";
        const data = JSON.parse(dataLine.slice(5).trim());

        if (event === "thinking") onThinking?.(data);
        else if (event === "delta") onDelta?.(data.text);
        else if (event === "done") onDone?.(data);
        else if (event === "error") onError?.(data.message);
      }
    }
  }

  _openStream().catch((err: { name?: string; message?: string }) => {
    if (err?.name !== "AbortError") onError?.(err?.message ?? "Stream error");
  });

  return () => ctrl.abort();
}
